import {
	hasLetter,
	hasDigit,
	hasSpecialCharacter,
	hasLowercase,
	hasUppercase,
} from './guards';
import type {TFunction} from 'next-i18next';

export enum PasswordWarning {
	TOO_SHORT = 'too_short',
	TOO_LONG = 'too_long',
	HAS_TRAILING_WHITESPACES = 'has_trailing_whitespaces',
	NO_UPPERCASE = 'no_uppercase',
	NO_LOWERCASE = 'no_lowercase',
	NO_DIGIT = 'no_digit',
	NO_SPECIAL_CHARACTER = 'no_special_character',
}

export enum PasswordLevel {
	WEAK = 'weak',
	MEDIUM = 'medium',
	STRONG = 'strong',
}

type PasswordValidation = {
	score: number;
	level: PasswordLevel;
	warning: PasswordWarning | null;
	getWarningMessage: (t: TFunction) => string;
};

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 50;

export const scorePassword = (password: string) => {
	if (!password) {
		return 0;
	}

	let score = 0;
	// Initialise score on letter variation range
	score += new Set(password.split('').filter(c => hasLetter(c))).size;

	// Shuffle score - bonus for messing things up.
	// Logical suites like abcde or 12345 are not attributed any points.
	// Messing things up with special characters increase the scoring.
	for (let i = 1; i < password.length; i++) {
		const previousChar = password[i - 1];
		const currentChar = password[i];

		const dist = Math.abs(
			previousChar.toLowerCase().charCodeAt(0) -
				currentChar.toLowerCase().charCodeAt(0),
		);

		// If the previous character is a letter and the next character
		// Is a digit or a special character, only consider as a character.
		// Another step is used to increase the scoring based on digits and special characters
		if (
			(hasLetter(previousChar) || hasLetter(currentChar)) &&
			(hasSpecialCharacter(previousChar) ||
				hasSpecialCharacter(currentChar) ||
				hasDigit(previousChar) ||
				hasDigit(currentChar))
		) {
			score += 5;
		} else if (
			(hasLowercase(previousChar) || hasLowercase(currentChar)) &&
			(hasUppercase(previousChar) || hasUppercase(currentChar))
		) {
			score += dist * 3;
		} else if (dist > 500) score += 50;
		else if (dist > 60) score += 15;
		else if (dist > 1) score += 5;
		else if (dist <= 1) score += 1;
	}

	// Compute character variation based on password prerequisite
	const variations = {
		digits: hasDigit(password),
		lower: hasLowercase(password),
		upper: hasUppercase(password),
		nonWords: hasSpecialCharacter(password),
	};

	const variationCount = Object.values(variations).reduce((acc, value) => {
		if (value) {
			return acc + 1;
		}
		return acc;
	}, 0);

	// Decrease the scoring when fewer variations are used in the password
	score -= Math.exp(4 - variationCount) * 5;

	// Special characters and numbers increase the scoring based on exponential terms
	score +=
		(score / 20) *
		Math.exp(password.split('').filter(c => hasDigit(c)).length / 1.2);
	score +=
		(score / 10) *
		Math.exp(
			password.split('').filter(c => hasSpecialCharacter(c)).length / 1.5,
		);

	// Bonus for password length
	score += (password.length - PASSWORD_MIN_LENGTH) * 3;

	return Math.max(0, Math.floor(score));
};

const getNormalisedScore = (score: number): number => {
	return Math.max(0, Math.min(score, 100));
};

const getPasswordLevel = (score: number): PasswordLevel => {
	if (score > 65) {
		return PasswordLevel.STRONG;
	}
	if (score >= 30) {
		return PasswordLevel.MEDIUM;
	}
	return PasswordLevel.WEAK;
};

const getPasswordWarning = (password: string): PasswordWarning | null => {
	if (password.length !== password.trim().length) {
		return PasswordWarning.HAS_TRAILING_WHITESPACES;
	}
	if (password.length < PASSWORD_MIN_LENGTH) {
		return PasswordWarning.TOO_SHORT;
	}
	if (password.length > PASSWORD_MAX_LENGTH) {
		return PasswordWarning.TOO_LONG;
	}
	if (!hasUppercase(password)) {
		return PasswordWarning.NO_UPPERCASE;
	}
	if (!hasLowercase(password)) {
		return PasswordWarning.NO_LOWERCASE;
	}
	if (!hasDigit(password)) {
		return PasswordWarning.NO_DIGIT;
	}
	if (!hasSpecialCharacter(password)) {
		return PasswordWarning.NO_SPECIAL_CHARACTER;
	}

	return null;
};

const getWarningMessage =
	(warningStatus: PasswordWarning | null) =>
	(t: TFunction): string => {
		switch (warningStatus) {
			case PasswordWarning.TOO_SHORT:
				return t('passwordrequirements_text_atleastXcharacters_d4c-web', {
					sprintf: [PASSWORD_MIN_LENGTH],
					count: PASSWORD_MIN_LENGTH,
				});
			case PasswordWarning.TOO_LONG:
				return t('passwordrequirements_text_nomorethanXcharacters_d4c-web', {
					sprintf: [PASSWORD_MAX_LENGTH],
					count: PASSWORD_MAX_LENGTH,
				});
			case PasswordWarning.HAS_TRAILING_WHITESPACES:
				return t(
					'passwordrequirements_text_cantbeginorendwithblankspace_d4c-web',
				);
			case PasswordWarning.NO_UPPERCASE:
				return t('passwordrequirements_text_atleastoneuppercaseletter_d4c-web');
			case PasswordWarning.NO_LOWERCASE:
				return t('passwordrequirements_text_atleastonelowercaseletter_d4c-web');
			case PasswordWarning.NO_DIGIT:
				return t('passwordrequirements_text_atleastonedigit_d4c-web');
			case PasswordWarning.NO_SPECIAL_CHARACTER:
				return t(
					'passwordrequirements_text_atleastonespecialcharacter_d4c-web',
				);
			default:
				return '';
		}
	};

export const passwordValidator = (password: string): PasswordValidation => {
	const score = getNormalisedScore(scorePassword(password));
	const warning = getPasswordWarning(password);
	return {
		score,
		level: getPasswordLevel(score),
		warning,
		getWarningMessage: getWarningMessage(warning),
	};
};

export default passwordValidator;
